import {
  type ComponentPropsWithoutRef,
  type ReactElement,
  type ReactNode,
  forwardRef,
  useEffect,
  useState,
  isValidElement,
  cloneElement,
} from "react"
import AppleAppStoreSvg from "./assets/apple-app-store-english-badge.svg"
import GooglePlayStoreSvg from "./assets/google-play-store-english-badge.svg"
import { type Icon } from "./iconCore"
import { Spinner } from "./spinner"
import { Check, Copy } from "./iconOutline"

function isReactElement(value: unknown): value is ReactElement {
  return isValidElement(value)
}

export const buttonVariants = [
  "primary",
  "secondary",
  "tertiary",
  "tertiary-gray",
  "destructive-primary",
  "destructive-secondary",
  "text",
  "text-destructive",
] as const

export const sizes = ["xs", "sm", "md"] as const

type Props = {
  children?: ReactNode
  /**
   * Indicates which variant of the button is being used. A variant represents
   * essentially different ways of styling the button.
   *
   * @default "primary"
   */
  variant?: (typeof buttonVariants)[number]

  /**
   * Indicates whether to display a loading spinner within the button.
   * This spinner will replace any icon that's being show with the `icon` prop.
   *
   * @default undefined
   */
  loading?: boolean

  /**
   * Expands the button to take of the full width of its parent
   */
  "data-full-width"?: boolean

  /**
   * Indicates the size of this button. Arbitrary sizes are not allowed so that
   * buttons are always given one of a narrow set of sizes that fit within
   * the design system
   *
   * @default "default"
   */
  size?: (typeof sizes)[number]

  /**
   * Indicates that this is button is only using an icon and it not accompanied
   * by a text label. This is behaves as a kind of subvariant when combined with
   * the data-variant property.
   */
  iconOnly?: boolean

  /**
   * Optionally indicates which icon to use as a leading icon before the button text,
   * or the icon for an icon-only button.
   */
  icon?: Icon | ReactElement

  /**
   * Optionally indicates which icon to use as a trailing icon after the button text.
   */
  trailingIcon?: Icon

  /**
   * If supplied, this adds a notification dot to the top-right of the button.
   * Usually for indicating a number related the button's content.
   *
   * Note: Callers must ensure the text supplied will fit within the dot.
   * The dot is always drawn as a circle: a 1 or 2 digit number will fit.
   */
  notificationDotText?: string
}

export type ButtonProps = ComponentPropsWithoutRef<"button"> & Props

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      iconOnly,
      loading,
      icon: Icon,
      trailingIcon: TrailingIcon,
      notificationDotText,
      children,
      ...restProps
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        data-loading={!!loading}
        data-variant={variant}
        data-size={size}
        data-icon-only={iconOnly}
        {...restProps}
        className={`button ${className ?? ""}`}
      >
        {loading && (
          <div className="spinner-container">
            <Spinner />
          </div>
        )}
        {Icon &&
          !loading &&
          (isReactElement(Icon) ? (
            cloneElement(Icon, {
              className: `icon ${Icon.props.className ?? ""}`,
            })
          ) : (
            <Icon />
          ))}
        {children}
        {TrailingIcon && <TrailingIcon />}
        {notificationDotText && (
          <span className="button-notification-dot body-sm">
            {notificationDotText}
          </span>
        )}
      </button>
    )
  },
)

Button.displayName = "Button"

export type LinkStyledAsButtonProps = ComponentPropsWithoutRef<"a"> & Props

export const LinkStyledAsButton = forwardRef<
  HTMLAnchorElement,
  LinkStyledAsButtonProps
>(
  (
    {
      children,
      className,
      variant,
      size,
      iconOnly,
      loading,
      icon: Icon,
      trailingIcon: TrailingIcon,
      ...restProps
    },
    ref,
  ) => {
    const iconSize = (size === "xs" || size === "sm") && !iconOnly ? "xs" : "sm"
    return (
      <a
        ref={ref}
        data-loading={!!loading}
        data-variant={variant}
        data-size={size}
        data-icon-only={iconOnly}
        {...restProps}
        className={`button ${className ?? ""}`}
      >
        {loading && (
          <div className="spinner-container">
            <Spinner data-size={iconSize} />
          </div>
        )}
        {Icon &&
          !loading &&
          (isReactElement(Icon) ? (
            cloneElement(Icon, {
              className: `icon ${Icon.props.className ?? ""}`,
              "data-size": iconSize,
            })
          ) : (
            <Icon data-size={iconSize} />
          ))}
        {children}
        {TrailingIcon && <TrailingIcon data-size={iconSize} />}
      </a>
    )
  },
)

LinkStyledAsButton.displayName = "LinkStyledAsButton"

export type AppStoreBadgeButtonProps = ComponentPropsWithoutRef<"button"> & {
  variant: "apple" | "google"
}

export const AppStoreButton = forwardRef<
  HTMLButtonElement,
  AppStoreBadgeButtonProps
>(({ className, variant, ...props }, ref) => {
  return (
    <button
      {...props}
      data-variant={variant}
      ref={ref}
      className={`app-store-button ${className ?? ""}`}
    >
      <div className="apple">
        <span className="screen-reader-only">Download on the App Store</span>
        <AppleAppStoreSvg />
      </div>
      <div className="google">
        <span className="screen-reader-only">Get it on Google Play</span>
        <GooglePlayStoreSvg />
      </div>
    </button>
  )
})

AppStoreButton.displayName = "AppStoreButton"

export type CopyButtonProps = Omit<ButtonProps, "icon" | "children"> & {
  /**
   * The value to copy to the clipboard.
   */
  value: string
}

export const CopyButton = forwardRef<HTMLButtonElement, CopyButtonProps>(
  ({ className, value, iconOnly, ...props }, ref) => {
    const [showCopiedMessage, setShowCopiedMessage] = useState(false)

    // The linter doesn't seem to like this, but it's necessary to ensure that
    // the timer is cleared when the component unmounts.
    // eslint-disable-next-line consistent-return
    useEffect(() => {
      if (showCopiedMessage) {
        const timer = setTimeout(() => setShowCopiedMessage(false), 2000)
        return () => clearTimeout(timer)
      }
    }, [showCopiedMessage])

    const handleCopy = () => {
      if (value) {
        navigator.clipboard
          .writeText(value)
          .then(() => setShowCopiedMessage(true))
          .catch((error) =>
            console.error("Failed to copy value to clipboard", error),
          )
      }
    }

    return (
      <Button
        {...props}
        ref={ref}
        iconOnly={iconOnly}
        icon={showCopiedMessage ? Check : Copy}
        onClick={handleCopy}
      >
        {!iconOnly && (showCopiedMessage ? "Copied" : "Copy")}
      </Button>
    )
  },
)

CopyButton.displayName = "CopyButton"

export type QuickActionCardProps = {
  children?: ReactNode

  featuredIcon?: ReactNode

  actionIcon?: Icon
}

export const QuickActionCard = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<"button"> & QuickActionCardProps
>(
  (
    { className, featuredIcon, actionIcon: ActionIcon, children, ...restProps },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        {...restProps}
        className={`quick-action-card title-lg text-color-primary-subdued ${
          className ?? ""
        }`}
      >
        {featuredIcon}
        {children}
        {ActionIcon && <ActionIcon />}
      </button>
    )
  },
)

QuickActionCard.displayName = "QuickActionCard"

export const LinkStyledAsQuickActionCard = forwardRef<
  HTMLAnchorElement,
  ComponentPropsWithoutRef<"a"> & QuickActionCardProps
>(
  (
    { className, featuredIcon, actionIcon: ActionIcon, children, ...restProps },
    ref,
  ) => {
    return (
      <a
        ref={ref}
        {...restProps}
        className={`quick-action-card title-lg text-color-primary-subdued ${
          className ?? ""
        }`}
      >
        {featuredIcon}
        {children}
        {ActionIcon && <ActionIcon />}
      </a>
    )
  },
)

LinkStyledAsQuickActionCard.displayName = "LinkStyledAsQuickActionCard"
